const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export interface KPICategory {
  id?: number;
  organization_id: number;
  category_name: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateKPICategoryRequest {
  category_name: string;
  organization_id?: number; // Optional, for super admins
}

export interface UpdateKPICategoryRequest {
  category_name: string;
}

export interface ImportResult {
  success: boolean;
  categoriesCreated: number;
  categoriesUpdated: number;
  kpisCreated: number;
  errors: ImportError[];
  warnings: ImportWarning[];
  summary: {
    totalRows: number;
    processedRows: number;
    skippedRows: number;
  };
}

export interface ImportError {
  row: number;
  field: string;
  message: string;
  severity: 'error' | 'warning';
  data?: any;
}

export interface ImportWarning {
  row: number;
  field: string;
  message: string;
  data?: any;
}

class KPICategoryService {
  private async getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  private async getAuthHeadersForFormData() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`
      // Don't set Content-Type for FormData - let browser set it with boundary
    };
  }

  async getKPICategories(): Promise<KPICategory[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/kpi-categories`, {
        method: 'GET',
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch KPI categories: ${response.statusText}`);
      }

      const data = await response.json();
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching KPI categories:', error);
      throw error;
    }
  }

  async createKPICategory(categoryData: CreateKPICategoryRequest): Promise<KPICategory> {
    try {
      const response = await fetch(`${API_BASE_URL}/kpi-categories`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(categoryData)
      });

      if (!response.ok) {
        throw new Error(`Failed to create KPI category: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to create KPI category');
      }

      return data.data;
    } catch (error) {
      console.error('Error creating KPI category:', error);
      throw error;
    }
  }

  async updateKPICategory(id: number, categoryData: UpdateKPICategoryRequest): Promise<KPICategory> {
    try {
      const response = await fetch(`${API_BASE_URL}/kpi-categories/${id}`, {
        method: 'PUT',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(categoryData)
      });

      if (!response.ok) {
        throw new Error(`Failed to update KPI category: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to update KPI category');
      }

      return data.data;
    } catch (error) {
      console.error('Error updating KPI category:', error);
      throw error;
    }
  }

  async deleteKPICategory(id: number): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/kpi-categories/${id}`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to delete KPI category: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to delete KPI category');
      }
    } catch (error) {
      console.error('Error deleting KPI category:', error);
      throw error;
    }
  }

  async importKPICategories(file: File): Promise<ImportResult> {
    try {
      const formData = new FormData();
      formData.append('csvFile', file);

      const response = await fetch(`${API_BASE_URL}/kpi-categories/import`, {
        method: 'POST',
        headers: await this.getAuthHeadersForFormData(),
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Import failed: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        // Even if success is false, we might have partial results
        return data.data || {
          success: false,
          categoriesCreated: 0,
          categoriesUpdated: 0,
          kpisCreated: 0,
          errors: [{ row: 0, field: 'Import', message: data.message || 'Import failed', severity: 'error' as const }],
          warnings: [],
          summary: { totalRows: 0, processedRows: 0, skippedRows: 0 }
        };
      }

      return data.data;
    } catch (error) {
      console.error('Error importing KPI categories:', error);
      throw error;
    }
  }
}

export default new KPICategoryService();